import React, { useState } from 'react';
import { Grid3X3, User, Heart } from 'lucide-react';

import GradientButtonTab from '@/components/ui/GradientButtonTab';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import GlassPill from '@/components/ui/GlassPill';

interface LibraryHeaderProps {
  showOnlyUserImages: boolean;
  showFavorites: boolean;
  onSetShowOnlyUserImages: (value: boolean) => void;
  onSetShowFavorites: (value: boolean) => void;
  isLoading?: boolean;
}

export const LibraryHeader: React.FC<LibraryHeaderProps> = ({
  showOnlyUserImages,
  showFavorites,
  onSetShowOnlyUserImages,
  onSetShowFavorites,
  isLoading = false
}) => {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  const handleFilterChange = (showUser: boolean, showFavs: boolean) => {
    onSetShowOnlyUserImages(showUser);
    onSetShowFavorites(showFavs);
  };

  const handleMobileTooltip = (tooltipId: string) => {
    // Only show mobile tooltips on touch devices (< sm breakpoint)
    if (window.innerWidth < 640) {
      setOpenTooltip(tooltipId);
      setTimeout(() => setOpenTooltip(null), 1000); // Show for 1 second
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex space-x-2 flex-1 md:flex-initial">
          <TooltipProvider delayDuration={300}>
            <Tooltip open={window.innerWidth < 640 ? openTooltip === 'all' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="blue"
                  isActive={!showOnlyUserImages && !showFavorites}
                  onClick={() => {
                    handleFilterChange(false, false);
                    handleMobileTooltip('all');
                  }}
                  icon={<Grid3X3 className="h-4 w-4" />}
                  testId="all-images-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  {/* CSS-only responsive text: show icon only on mobile, full text on larger screens */}
                  <span className="hidden sm:inline">All Images</span>
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border border-white/20 shadow-lg">
                View All Images
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider delayDuration={300}>
            <Tooltip open={window.innerWidth < 640 ? openTooltip === 'user' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="purple"
                  isActive={showOnlyUserImages}
                  onClick={() => {
                    handleFilterChange(true, false);
                    handleMobileTooltip('user');
                  }}
                  icon={<User className="h-4 w-4" />}
                  testId="your-images-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  {/* CSS-only responsive text: show icon only on mobile, full text on larger screens */}
                  <span className="hidden sm:inline">Your Images</span>
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border border-white/20 shadow-lg">
                View Your Creations
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider delayDuration={300}>
            <Tooltip open={window.innerWidth < 640 ? openTooltip === 'favorites' : undefined}>
              <TooltipTrigger asChild>
                <GradientButtonTab
                  variant="red"
                  isActive={showFavorites}
                  onClick={() => {
                    handleFilterChange(false, true);
                    handleMobileTooltip('favorites');
                  }}
                  icon={<Heart className="h-4 w-4" />}
                  testId="favorites-button"
                  fullWidth={true}
                  className="md:w-auto"
                >
                  {/* CSS-only responsive text: show icon only on mobile, full text on larger screens */}
                  <span className="hidden sm:inline">Favorites</span>
                </GradientButtonTab>
              </TooltipTrigger>
              <TooltipContent className="bg-gradient-to-br from-red-500 to-red-600 text-white border border-white/20 shadow-lg">
                View Your Favorites
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Header/Loading indicator - CSS-only: Hidden on mobile, visible on md+ */}
        <div 
          className="hidden md:flex items-center" 
          data-test-id="library-header-indicator"
        >
          <div className={cn(isLoading && "animate-pulse")}>
            <GlassPill
              type={isLoading 
                ? "Loading..." 
                : showOnlyUserImages 
                  ? "Your Creations" 
                  : showFavorites 
                    ? "Your Favorites"
                    : "All Images"}
              variant="blue"
              className="text-sm font-medium bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent border-blue-200/50 dark:border-blue-800/30"
            />
          </div>
        </div>
      </div>
      
      
    </>
  );
};
